import { useEffect, useMemo, useRef, useState } from "react";
import copyToClipboard from "copy-to-clipboard";
import { t } from "@/i18n-js/instance";
import {
  ROOM_ACCESS_TYPES,
  isLimitUrlSharingEnabled,
} from "@circle-react/helpers/liveStreamRoomHelpers";
import { liveStreamPaths } from "@circle-react/helpers/urlHelpers";
import type { Room } from "@circle-react/types/Live/Room";
import { useToast } from "@circle-react-shared/uikit/ToastV2";
import { Toolbar } from "../../../Toolbar";

const COPIED_MODE_DURATION = 3000;

export interface CopyLinkButtonProps {
  room: Pick<Room, "slug" | "access_type" | "limit_url_sharing">;
}

export const CopyLinkButton = ({ room }: CopyLinkButtonProps) => {
  const { success } = useToast();
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const link = useMemo(() => {
    if (room?.access_type === ROOM_ACCESS_TYPES.PUBLIC_STREAM) {
      return `${window.location.origin}${liveStreamPaths.publicRoom({
        roomSlug: room?.slug,
      })}`;
    }
    return window.location.href;
  }, [room?.slug, room?.access_type]);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  const toggleIsCopiedMode = () => {
    setIsCopied(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsCopied(false);
    }, COPIED_MODE_DURATION);
  };

  const handleOnClick = () => {
    copyToClipboard(link);
    toggleIsCopiedMode();
    success(t("share_link.link_copied"), {
      duration: "short",
      shouldUseIndefiniteIcon: true,
    });
  };

  if (isLimitUrlSharingEnabled(room)) {
    return null;
  }

  return (
    <Toolbar.IconButton
      icon={isCopied ? "16-check-circle" : "live-link"}
      tooltipText={
        isCopied
          ? t("share_link.link_copied")
          : t("share_link.copy_link_to_clipboard")
      }
      onClick={handleOnClick}
    />
  );
};
